import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { slugify } from "../../../utils/slugify"
import BookCabForm from "../BookCabForm"
import PaymentCard from "../PaymentCard"
import Hatchback from "../../../assets/cars/hatchback.webp"
import Sedan from "../../../assets/cars/sedan.webp"
import SUV from "../../../assets/cars/suv.webp"
import ProgressStepper from "../../../components/ProgressStepper"

const getCarImage = (name) => {
  const n = (name || "").toLowerCase().replace(/\s+/g, "")
  if (n === "hatchback") return Hatchback
  if (n === "suv") return SUV
  return Sedan
}

const CAB_TYPE = { 1: "COMPACT", 2: "SEDAN", 3: "SUV" }

const MODEL_NAMES = {
  1: "Swift, WagonR or similar",
  2: "Dzire, Etios or similar",
  3: "Innova, Ertiga or similar"
}

const BookCabMain = ({ restoredTripData = null }) => {
  const navigate = useNavigate()
  const tripData = restoredTripData || JSON.parse(localStorage.getItem("tripData") || "null")
  const { distance } = useSelector((state) => state.cab || {})
  const [showDetails, setShowDetails] = useState(false)
  const [activeTab, setActiveTab] = useState("info")

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return ""
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
  }

  const formatDisplayTime = (timeStr) => {
    if (!timeStr) return ""
    if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) {
      return timeStr
    }
    const [h, m] = timeStr.split(":").map(Number)
    if (isNaN(h)) return timeStr
    const ampm = h >= 12 ? "PM" : "AM"
    const h12 = h % 12 || 12
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`
  }

  useEffect(() => {
    const stored = localStorage.getItem("tripData")
    if (!stored && !restoredTripData) navigate("/")
    window.scrollTo(0, 0)
  }, [navigate, restoredTripData])

  const handleExpiryRedirect = () => {
    if (tripData) {
      const from = slugify(tripData.pickup_city)
      const to = tripData.drop_city ? slugify(tripData.drop_city) : null
      
      if (from && to) navigate(`/routes/${from}-to-${to}-cab/`)
      else if (from) navigate(`/cities/${from}/`)
      else navigate("/")
    } else {
      navigate("/")
    }
  }

  if (!tripData) return (
    <div className="pt-[100px] text-center font-bold text-gray-500">
      <p>Loading booking details...</p>
    </div>
  )

  const cab = tripData.cab
  const cabType = CAB_TYPE[cab?.vehicle_id] || "CAB"
  const displayName = cab?.name === "Hatchback" ? "Hatch Back" : cab?.name

  return (
    <div className="bg-[#f9fafa] text-[#171d1b] min-h-screen font-body pb-24">
      {/* Header */}
      <nav className="w-full bg-white border-b border-gray-100 flex items-center justify-between px-4 md:px-12 h-16 md:h-24 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <button onClick={handleExpiryRedirect} className="flex items-center shrink-0">
          <img src="/images/Newlogo.webp" alt="Pulpit Cabs" className="h-[36px] md:h-[54px] w-auto" />
        </button>

        <a href="tel:+918052080501" className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity duration-200 shrink-0 ml-3">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2" className="shrink-0 md:w-[24px] md:h-[24px]"><path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          <div className="flex flex-col">
            <span className="text-[9px] md:text-[10px] text-[#6b7280] font-bold uppercase tracking-wider leading-tight">Need Help?</span>
            <span className="text-[14px] md:text-lg font-black text-[#171d1b] leading-tight tracking-tight whitespace-nowrap">+91 80520-80501</span>
          </div>
        </a>
      </nav>

      {/* Progress Stepper */}
      <ProgressStepper currentStep={3} />

      <main className="max-w-[1080px] mx-auto px-4 md:px-8 mt-6 md:mt-10">
        {/* Title Section */}
        <header className="mb-5 md:mb-8">
          <h1 className="text-[26px] md:text-[46px] font-black tracking-tight text-[#1a1a1a] mb-2 leading-none">
            Review Booking
          </h1>
          <div className="flex flex-wrap items-center gap-2 md:gap-3 text-[#555] font-bold text-[12px] md:text-[15px]">
            <span>{tripData.pickup_city}</span>
            {tripData.drop_city && (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                <span>{tripData.drop_city}</span>
              </>
            )}
            <span className="mx-1 md:mx-2 text-[#ccc]">|</span>
            <div className="flex items-center gap-1 md:gap-1.5 bg-[#21A5A0]/10 text-[#21A5A0] px-2 py-0.5 md:px-2.5 md:py-1 rounded-md text-[11px] md:text-[13px]">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <span className="font-bold tracking-wide">{formatDisplayTime(tripData.trip_time)} | {formatDisplayDate(tripData.trip_date)}</span>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Details & Form */}
          <div className="lg:col-span-8 flex flex-col gap-6">
            
            {/* Cab Details Card */}
            <div className="bg-white rounded-[24px] p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.03)] border border-gray-50 flex flex-row items-center justify-between overflow-hidden transition-all duration-300 hover:shadow-[0_8px_30px_rgba(33,165,160,0.12)] hover:border-[#21A5A0]/30 hover:-translate-y-0.5">
              <div className="flex-1 min-w-0 pr-3 md:pr-4 relative z-10">
                <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-6">
                  <span className="px-2 md:px-3 border-2 border-[#21A5A0] text-[#21A5A0] font-black text-[10px] md:text-[11px] uppercase tracking-wider py-0.5 md:py-1 rounded-full whitespace-nowrap">
                    {cabType}
                  </span>
                  <button
                    onClick={handleExpiryRedirect}
                    className="flex items-center gap-1 text-[#21A5A0] font-bold text-[11px] md:text-[12px] border border-[#21A5A0]/30 hover:bg-[#21A5A0]/5 px-2 md:px-3 py-0.5 md:py-1 rounded-full transition-colors whitespace-nowrap"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    Switch Cab
                  </button>
                </div>

                <h2 className="text-[22px] md:text-[34px] font-black text-[#1a1a1a] leading-none mb-1 truncate">
                  {displayName}
                </h2>
                <div className="text-[10px] md:text-[11px] font-black text-[#21A5A0] uppercase tracking-widest mb-3 md:mb-6 border-b border-gray-100 pb-2 md:pb-5 w-max max-w-full truncate">
                  {MODEL_NAMES[cab?.vehicle_id] || "Swift, WagonR"}
                </div>

                {/* Features inline */}
                <div className="flex flex-wrap items-center gap-1.5 md:gap-3">
                  <div className="flex items-center gap-1 md:gap-1.5 bg-[#f4f7f6] border border-[#e8edec] px-2 py-1 md:px-3 md:py-1.5 rounded-full text-[11px] md:text-[13px] font-bold text-[#3d4946]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    {cab?.seat_capacity} Seats
                  </div>
                  <div className="flex items-center gap-1 md:gap-1.5 bg-[#f4f7f6] border border-[#e8edec] px-2 py-1 md:px-3 md:py-1.5 rounded-full text-[11px] md:text-[13px] font-bold text-[#3d4946]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
                    2 Bags
                  </div>
                  <div className="flex items-center gap-1 md:gap-1.5 bg-[#eef9f9] border border-[#d4edec] px-2 py-1 md:px-3 md:py-1.5 rounded-full text-[11px] md:text-[13px] font-bold text-[#3d4946]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5"><path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/></svg>
                    AC
                  </div>
                </div>
              </div>

              {/* Car Image */}
              <div className="w-[110px] md:w-[280px] shrink-0 flex items-center justify-center relative group perspective-1000">
                <div className="absolute w-[100px] h-[100px] md:w-[220px] md:h-[220px] rounded-full bg-gradient-to-tr from-[#f4f7f6] to-[#eef9f9] -z-10 blur-xl"></div>
                <img
                  src={getCarImage(cab?.name)}
                  alt={displayName}
                  className="w-full max-w-[110px] md:max-w-[280px] object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.10)] md:drop-shadow-[0_12px_24px_rgba(0,0,0,0.12)] md:-mr-4 transition-transform duration-500 group-hover:scale-90 group-hover:-translate-y-2"
                />
              </div>
            </div>

            {/* What's Included Section */}
            <div className="bg-white rounded-[24px] p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.03)] border border-gray-50 transition-all duration-300 hover:shadow-[0_12px_40px_rgba(33,165,160,0.08)]">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 mb-4 md:mb-8">
                <div>
                  <h3 className="text-[18px] md:text-[24px] font-black text-[#1a1a1a] leading-tight">Included in your fare</h3>
                  <p className="text-[12px] md:text-[13px] font-medium text-[#777] mt-1">Transparency is our priority. No hidden surprises.</p>
                </div>
                <div className="px-3 py-1.5 md:px-4 md:py-2 bg-[#f0f6f5] rounded-xl border border-[#21A5A0]/10 flex items-center gap-2 w-fit">
                  <div className="w-2 h-2 rounded-full bg-[#21A5A0] animate-pulse"></div>
                  <span className="text-[11px] font-black text-[#21A5A0] uppercase tracking-wider">Verified Listing</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 md:gap-x-12 gap-y-4 md:gap-y-6 mb-4 md:mb-8">
                {[
                  {
                    title: "GST & Taxes",
                    desc: "State taxes and GST are fully covered",
                    icon: <><path d="M19 5L5 19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>

                  },
                  {
                    title: "Air Conditioning",
                    desc: "Premium climate control for your comfort",
                    icon: <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/>
                  },
                  {
                    title: "Real-time Tracking",
                    desc: "Share your trip status with loved ones",
                    icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>
                  },
                  {
                    title: "Professional Captain",
                    desc: "Experienced and background-verified drivers",
                    icon: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>
                  },
                  {
                    title: "Grace Period",
                    desc: "15 minutes of free waiting at pickup",
                    icon: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>
                  },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 md:gap-4 group">
                    <div className="w-9 h-9 md:w-11 md:h-11 rounded-2xl bg-[#f8faf9] border border-[#eef2f1] flex items-center justify-center shrink-0 transition-all group-hover:border-[#21A5A0]/30 group-hover:bg-[#f0f6f5]">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {item.icon}
                      </svg>
                    </div>
                    <div>
                      <div className="text-[13px] md:text-[15px] font-black text-[#1a1a1a] mb-0.5">{item.title}</div>
                      <div className="text-[11px] md:text-[12.5px] font-medium text-[#777] leading-tight">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4 md:pt-6 border-t border-dashed border-gray-200">
                <div className="flex flex-col md:flex-row gap-4 md:gap-6 md:items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5 md:mb-2">
                      <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <span className="text-[13px] md:text-[14px] font-black text-[#1a1a1a]">Flexible Cancellation</span>
                    </div>
                    <p className="text-[11px] md:text-[12px] font-medium text-[#777] leading-relaxed">
                      Free cancellation up to 4 hours before pickup. Cancellations within 4 hours may incur a fee as per our policy.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3 bg-[#FFF6E9] px-3 py-2 md:px-4 md:py-3 rounded-2xl border border-[#FDE5C0] w-fit">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E67F00" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <span className="text-[11px] font-black text-[#E67F00] uppercase tracking-wide">Tolls & Parking Excluded</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Traveller Form — primary action, shown first */}
            <div className="bg-[#f0f6f5] rounded-[24px] p-4 md:p-8 border border-[#e8edec] transition-all duration-300 hover:shadow-[0_8px_30px_rgba(33,165,160,0.12)] hover:border-[#21A5A0]/30 hover:-translate-y-0.5">
              <BookCabForm />
            </div>

            {/* Driver Details & Inclusions/Exclusions — secondary, shown below */}
            <div className={`bg-[#f0f6f5] rounded-[24px] border border-[#e8edec] transition-all duration-300 overflow-hidden hover:shadow-[0_8px_30px_rgba(33,165,160,0.12)] hover:border-[#21A5A0]/30 hover:-translate-y-0.5 ${showDetails ? 'pb-8' : ''}`}>
              <div 
                className="flex items-center justify-between p-6 md:p-8 cursor-pointer select-none"
                onClick={() => setShowDetails(!showDetails)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#21A5A0]/10 flex items-center justify-center shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                    </svg>
                  </div>
                  <h3 className="text-[16px] md:text-[20px] font-black text-[#1a1a1a]">Driver & Cab Details</h3>
                </div>
                <div className={`transform transition-transform duration-300 ${showDetails ? 'rotate-180' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </div>

              {/* Accordion Body */}
              <div 
                className={`transition-all duration-500 ease-in-out px-6 md:px-8 ${
                  showDetails ? "opacity-100 max-h-[1000px]" : "opacity-0 max-h-0"
                }`}
              >
                {/* Tab Headers */}
                <div className="flex gap-5 md:gap-8 border-b border-gray-100 mb-6 md:ml-[52px]">
                  <button
                    onClick={() => setActiveTab("info")}
                    className={`pb-3 text-[12px] md:text-[14px] font-black uppercase tracking-wider transition-all relative ${
                      activeTab === "info" ? "text-[#21A5A0]" : "text-gray-400"
                    }`}
                  >
                    Cab Info
                    {activeTab === "info" && <div className="absolute bottom-0 left-0 w-full h-1 bg-[#21A5A0] rounded-full"></div>}
                  </button>
                  <button
                    onClick={() => setActiveTab("inclusions")}
                    className={`pb-3 text-[12px] md:text-[14px] font-black uppercase tracking-wider transition-all relative ${
                      activeTab === "inclusions" ? "text-[#21A5A0]" : "text-gray-400"
                    }`}
                  >
                    Inclusions
                    {activeTab === "inclusions" && <div className="absolute bottom-0 left-0 w-full h-1 bg-[#21A5A0] rounded-full"></div>}
                  </button>
                </div>

                {activeTab === "info" ? (
                  <div className="pb-4">
                    <p className="text-[13px] md:text-[14px] font-medium text-[#3d4946] mb-1 md:pl-[52px]">Cab operator will be assigned as soon as booking is complete.</p>
                    <p className="text-[12px] md:text-[13px] font-medium text-[#777] mb-6 md:pl-[52px]">
                      Your cab and driver details will be shared via Whatsapp & SMS on your registered mobile number by <span className="font-bold text-[#3d4946]">{formatDisplayTime(tripData.trip_time)} | {formatDisplayDate(tripData.trip_date)}</span>
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 md:ml-[52px] pt-2 pb-6">
                    {/* Inclusions */}
                    <div>
                      <h4 className="text-[12px] md:text-[14px] font-black text-[#1a1a1a] uppercase tracking-wider mb-3 md:mb-4">Inclusions</h4>
                      <ul className="space-y-2 md:space-y-3">
                        <li className="flex items-center gap-2 md:gap-3 text-[13px] md:text-[14px] font-medium text-[#3d4946]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                          Base Fare upto {Math.ceil(distance || cab?.distance || 0)} KM
                        </li>
                        <li className="flex items-center gap-2 md:gap-3 text-[13px] md:text-[14px] font-medium text-[#3d4946]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                          Driver Allowance
                        </li>
                        <li className="flex items-center gap-2 md:gap-3 text-[13px] md:text-[14px] font-medium text-[#3d4946]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                          Night Allowance
                        </li>
                        <li className="flex items-center gap-2 md:gap-3 text-[13px] md:text-[14px] font-medium text-[#3d4946]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                          GST {cab?.gst_percentage || 5}%
                        </li>
                      </ul>
                    </div>

                    {/* Exclusions */}
                    <div>
                      <h4 className="text-[12px] md:text-[14px] font-black text-[#1a1a1a] uppercase tracking-wider mb-3 md:mb-4">Exclusions</h4>
                      <ul className="space-y-2 md:space-y-3">
                        <li className="flex items-center gap-2 md:gap-3 text-[13px] md:text-[14px] font-medium text-[#777]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          Extra ₹{cab?.fare_per_km || 10}/Km
                        </li>
                        <li className="flex items-center gap-2 md:gap-3 text-[13px] md:text-[14px] font-medium text-[#777]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          State Charges & Toll Tax
                        </li>
                        <li className="flex items-center gap-2 md:gap-3 text-[13px] md:text-[14px] font-medium text-[#777]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          Parking
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Column: Payment */}
          <div className="lg:col-span-4 h-full">
            <PaymentCard tripData={tripData} />
          </div>
        </div>
      </main>
    </div>
  )
}

export default BookCabMain
